import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPostFormDataWithProgress,
} from "../../client";
import { ApiConfig } from "../../config";
import { buildQueryString } from "../../utils";

const PATIENTS_ROOT = `${ApiConfig.CLINIC_BASE}/patients/`;

/**
 * Search patients by keyword. Supports name, HN, phone, or ID number searches.
 */
export async function searchPatients(
  keyword,
  { page = 1, pageSize = 10 } = {},
) {
  const queryString = buildQueryString({
    query: keyword,
    page,
    page_size: pageSize,
  });
  return apiGet(`${PATIENTS_ROOT}${queryString}`);
}

/**
 * Fetch a paginated list of patients. Defaults to page 1 with page size 10.
 */
export async function fetchPatients({ page = 1, pageSize = 10 } = {}) {
  const queryString = buildQueryString({ page, page_size: pageSize });
  return apiGet(`${PATIENTS_ROOT}${queryString}`);
}

export async function fetchPreviousVisit(
  patientId,
  { page = 1, pageSize = 5 } = {},
) {
  if (!patientId) {
    throw new Error("patientId is required");
  }
  const queryString = buildQueryString({ page, page_size: pageSize });
  return apiGet(`${PATIENTS_ROOT}${patientId}/treatments/${queryString}`);
}
/**
 * Retrieve the detailed profile of a patient by id.
 */
export async function fetchPatientProfile(patientId) {
  //
  if (!patientId) {
    throw new Error("patientId is required");
  }
  return apiGet(
    `${ApiConfig.CLINIC_BASE}/staff/patient-management/profile/${patientId}/`,
  );
}

/**
 * Create a new patient record using the provided payload.
 */
export async function createPatient(payload) {
  return apiPost(PATIENTS_ROOT, payload);
}

/**
 * Create a new patient visit using the provided payload.
 */
export async function createVisit(payload) {
  return apiPost(`${ApiConfig.CLINIC_BASE}/visits/`, payload);
}

/**
 * Create a patient session for registration or queue flows.
 */
export async function createPatientSession(patientId) {
  const payload = patientId ? { patient_id: Number(patientId) } : {};
  return apiPost(`${ApiConfig.CLINIC_BASE}/patient/patient-sessions/`, payload);
}

/**
 * Create a new patient information or update an existing one using the provided payload.
 */
export async function createOrUpdatePatient(payload) {
  return apiPost(
    `${ApiConfig.CLINIC_BASE}/patient/create-or-update-patient/`,
    payload,
  );
}

/**
 * Retrieve the detailed profile of a patient by identity (id_card or passport_number).
 */
export async function fetchPatientProfileByIdentity(
  identityType,
  identityDetail,
) {
  if (!identityType || !identityDetail) {
    throw new Error("identityType and identityDetail are required");
  }
  const queryString = buildQueryString({ [identityType]: identityDetail });
  return apiGet(`${ApiConfig.CLINIC_BASE}/patient/patients/${queryString}`);
}

/**
 * Check identity of patient from id card or passport number.
 */
export async function checkUsersIdentity(identityType, identityDetail) {
  if (!identityType || !identityDetail) {
    throw new Error("identityType and identityDetail is required");
  }
  const queryString = buildQueryString({ [identityType]: identityDetail });
  return apiGet(`${ApiConfig.CLINIC_BASE}/users/check-identity/${queryString}`);
}

/**
 * Retrieve treatment details by treatment ID.
 */
export async function fetchTreatment(treatmentId) {
  if (!treatmentId) {
    throw new Error("treatmentId is required");
  }
  return apiGet(`${ApiConfig.CLINIC_BASE}/treatments/${treatmentId}/`);
}

/**
 * Retrieve symptom assessment details by symptom assessment ID.
 */
export async function fetchSymptomAssestmentDetail(symptomAssessmentId) {
  if (!symptomAssessmentId) {
    throw new Error("symptomAssessmentId is required");
  }
  return apiGet(
    `${ApiConfig.CLINIC_BASE}/symptom-assessments/${symptomAssessmentId}`,
  );
}

/**
 * Update symptom assessment details by symptom assessment ID.
 */
export async function updateSymptomAssestmentDetail(
  symptomAssessmentId,
  payload,
) {
  if (!symptomAssessmentId) {
    throw new Error("symptomAssessmentId is required");
  }
  return apiPatch(
    `${ApiConfig.CLINIC_BASE}/symptom-assessments/${symptomAssessmentId}/`,
    payload,
  );
}

/**
 * Retrieve symptom assessment details by patient ID.
 */
export async function fetchSymptomAssestmentDetailFromPatientId(patientId) {
  //
  if (!patientId) {
    throw new Error("patientId is required");
  }
  return apiGet(
    `${ApiConfig.CLINIC_BASE}/patients/${patientId}/symptom-assessments/current/`,
  );
}

/**
 * Request patient consent.
 */
export async function requestPatientConsent(patientId) {
  if (!patientId) {
    throw new Error("patientId is required");
  }
  return apiGet(
    `${ApiConfig.CLINIC_BASE}/patient/${patientId}/request-consent/`,
  );
}

/**
 * Clear consent terminal.
 */
export async function clearConsentTerminal() {
  return apiPost(`${ApiConfig.CLINIC_BASE}/consents/terminal/clear/`);
}

/**
 * Get payer list.
 */
export async function getPayerList() {
  return apiGet(`${ApiConfig.CLINIC_BASE}/payer-plans/`);
}

/**
 * Get payer list with patientId.
 */
export async function getPatientPayerList(patientId) {
  return apiGet(`${ApiConfig.CLINIC_BASE}/patients/${patientId}/payers/`);
}

export async function updateCurrentPayer(patientId, patientPayerId) {
  const payload = {
    patient_payer_id: patientPayerId, //ส่ง null มากรณีชำระเอง
  };
  return apiPatch(
    `${ApiConfig.CLINIC_BASE}/patients/${patientId}/current-payer/`,
    payload,
  );
}

/**
 * Staff accepts registration consent on behalf of the patient.
 */
export async function staffAcceptRegisterConsent(patientId) {
  if (!patientId) {
    throw new Error("patientId is required");
  }
  return apiPost(
    `${ApiConfig.CLINIC_BASE}/patients/${patientId}/registration-consents/accept/`,
  );
}

/**
 * Save a draft screening record for a patient.
 * Creates or updates a PatientQueue entry with status "draft" (รอดำเนินการ)
 * without requiring patient consent.
 */
export async function saveDraftScreening(patientId, payload) {
  const normalizedPatientId = Number(patientId);
  if (!Number.isFinite(normalizedPatientId) || normalizedPatientId <= 0) {
    throw new Error("valid patientId is required");
  }
  return apiPost(`${ApiConfig.CLINIC_BASE}/visits/`, {
    ...(payload ?? {}),
    patient_id: normalizedPatientId,
    is_draft: true,
  });
}

/**
 * Get nationality choices from API.
 */
export async function getNationalityChoices() {
  return apiGet(`${ApiConfig.CLINIC_BASE}/nationalities/`);
}

/**
 * Fetch a specific patient attachment.
 */
export async function fetchPatientAttachment(attachmentId) {
  if (!attachmentId) {
    throw new Error("attachmentId is required");
  }
  return apiGet(`${ApiConfig.CLINIC_BASE}/attachments/${attachmentId}/`);
}

/**
 * Upload patient attachments with progress.
 */
export async function uploadPatientAttachmentsWithProgress(
  formData,
  onProgress,
) {
  return apiPostFormDataWithProgress(
    `${ApiConfig.CLINIC_BASE}/attachments/`,
    formData,
    onProgress,
  );
}

/**
 * Update patient attachment (note or category).
 */
export async function updatePatientAttachment(attachmentId, payload) {
  if (!attachmentId) {
    throw new Error("attachmentId is required");
  }
  return apiPatch(
    `${ApiConfig.CLINIC_BASE}/attachments/${attachmentId}/`,
    payload,
  );
}

/**
 * Delete patient attachment.
 */
export async function deletePatientAttachment(attachmentId, options = {}) {
  if (!attachmentId) {
    throw new Error("attachmentId is required");
  }
  const query = options?.type ? `?type=${options.type}` : "";
  return apiDelete(`${ApiConfig.CLINIC_BASE}/attachments/${attachmentId}/${query}`);
}
